import { Component, Input, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalizationService } from '@app/core';
import { eyeIconOutline, timeIconOutline } from '@app/shared/common/constants';
import { AccountLinkTypeEnum, ApplicationStatus, EGeneralApplicationFormAccountLinkType, EGeneralApplicationFormAccountLinkTypeURL, LanguageEnum, PositionStepKanbanStaticTypes } from '@app/shared/common/enums';
import { ApplicantDetailStepScoreCommunicationService } from '@app/vinter/services/applicant-detail-stepscore-communation.service';
import { ApplicationService } from '@app/vinter/services/application.service';
import { PositionService } from '@app/vinter/services/position.service';
import { Subscription, firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-applicant-profile',
    templateUrl: './applicant-profile.component.html',
    styleUrls: ['./applicant-profile.component.scss']
})
export class ApplicantProfileComponent implements OnInit, OnDestroy {
 
    @Input() applicationId: string;
    applicant: any = {};
    loading: boolean = true;

    iconPath = 'assets/vinter/icons/';
    iconPathEnding = '_input.svg';
    currentStepIdTemp: string = null;
    isEmailCopied = false;
    applicationStepId: string;
    changedTab: string;
    socialPlatformsDialogVisible = false;
    eGeneralApplicationFormAccountLinkType = EGeneralApplicationFormAccountLinkType;
    timeIcon = timeIconOutline;
    eyeIconOutline = eyeIconOutline;
    positionId: string;
    kanbanSteps: any[];
    data: any;
    isStepChangeSelectionDisabled = true;
    selectableStepList = [];
    isStaticStep: boolean = false;
    private VinterScoreUpdateSub!: Subscription;
    positionStepKanbanStaticTypes = PositionStepKanbanStaticTypes;
    applicationStatus = ApplicationStatus;
    changeStepDialogVisible = false;

    constructor(public applicationService: ApplicationService,
        private positionService: PositionService,
        public route: ActivatedRoute,
        public translate: LocalizationService,
        private communicationService: ApplicantDetailStepScoreCommunicationService,
        private cdr: ChangeDetectorRef
    ) {
    }

    isStaticKanbanStep(stepId: string): boolean {
        return stepId === PositionStepKanbanStaticTypes.Archived ||
            stepId === PositionStepKanbanStaticTypes.Rejected ||
            stepId === PositionStepKanbanStaticTypes.Invitation ||
            stepId === PositionStepKanbanStaticTypes.Hired;
    }

    isSpecialKanbanStep(stepId: string): boolean {
        if (!stepId) return false;
        
        return stepId === PositionStepKanbanStaticTypes.Invitation ||
            stepId === PositionStepKanbanStaticTypes.Archived ||
            stepId === PositionStepKanbanStaticTypes.Rejected ||
            stepId === PositionStepKanbanStaticTypes.Offer ||
            stepId === PositionStepKanbanStaticTypes.Hired ||
            stepId === PositionStepKanbanStaticTypes.ShortList ||
            stepId === PositionStepKanbanStaticTypes.GeneralApplication;
    }

    mapApplicationStatusToKanbanStep(status: any): string {
        if (status === null || status === undefined) return null;
        const statusStr = status.toString();
        
        switch (statusStr) {
            case ApplicationStatus.HRRejected:
                return PositionStepKanbanStaticTypes.Rejected;
            case ApplicationStatus.HRArchived:
                return PositionStepKanbanStaticTypes.Archived;
            case ApplicationStatus.HROffer:
                return PositionStepKanbanStaticTypes.Offer;
            case ApplicationStatus.Hired:
                return PositionStepKanbanStaticTypes.Hired;
            case ApplicationStatus.ShortList:
                return PositionStepKanbanStaticTypes.ShortList;
            default:
                return null;
        }
    }

    updateStepChangeDisabled() {
        this.isStaticStep = this.isStaticKanbanStep(this.applicant?.currentStepId);
        return this.isStaticStep;
    }

    applicantStepChanged(event) {
        var applicantStepChangeData = {
            previousApplicationStep: {
                Id: this.applicant.currentStepId,
                applicationId: this.applicationId,
            },
            nextPositionStepID: event.value,
            isArchived: false,
        }
                
        this.positionService.changeApplicationStepByKanban(applicantStepChangeData).subscribe({
            next: (response) => {
                this.data = this.applicant.currentStepId;
                this.applicant.currentStepId = event.value;
                this.currentStepIdTemp = event.value;
                this.updateStepChangeDisabled();
            },
            error: (error) => {
                this.changeStepDialogVisible = true;
                this.cdr.detectChanges();
      }
        });
    }
    
    handleCloseDialog(event: boolean): void {
        this.changeStepDialogVisible = event;
        if (!event) {
            if (this.applicant) {
                this.currentStepIdTemp = this.applicant.currentStepId;
                this.cdr.detectChanges();
            }
        }
    }

    async ngOnInit() {
        this.loading = true;
        if (this.applicationId) {
            await this.getApplicationProfileData();
            this.setApplicantUrlList();
            this.translateAvailableStepListTitles();
            this.updateStepChangeDisabled();
            this.cdr.detectChanges();
        } else {
            this.loading = false;
        }
        this.VinterScoreUpdateSub = this.communicationService.profileUpdate$.subscribe(() => {
            this.getApplicationProfileData();
        });
    }
   
    ngOnDestroy(): void {
        if (this.VinterScoreUpdateSub) {
            this.VinterScoreUpdateSub.unsubscribe();
        }
    }
    lastActivityDiff() {
        let currentDate = new Date();
        let loginDate = new Date(this.applicant.lastLoginDate);
        let timeDiff = Math.abs(currentDate.getTime() - loginDate.getTime());
        this.applicant.daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    }

    private getKanbanStepTitle(stepId: string): string {
        switch (stepId) {
            case PositionStepKanbanStaticTypes.Archived:
                return this.translate.translateText("HRArchived");
            case PositionStepKanbanStaticTypes.Rejected:
                return this.translate.translateText("HRRejected");
            case PositionStepKanbanStaticTypes.Invitation:
                return this.translate.translateText("HRInvitation");
            case PositionStepKanbanStaticTypes.Hired:
                return this.translate.translateText("Hired");
            case PositionStepKanbanStaticTypes.Offer:
                return this.translate.translateText("HROffer");
            case PositionStepKanbanStaticTypes.ShortList:
                return this.translate.translateText("ShortList");
            case PositionStepKanbanStaticTypes.GeneralApplication:
                return this.translate.translateText("GeneralApplication");
            default:
                return stepId;
        }
    }

    private addStepToAvailableList(stepId: string): void {
        if (!stepId || this.applicant.availableStepList.some(step => step.stepId === stepId)) {
            return;
        }

        this.applicant.availableStepList.push({
            stepId: stepId,
            title: this.getKanbanStepTitle(stepId)
        });
    }

    private handleCurrentStep(mappedKanbanStepId: string): void {
        if (this.applicant.currentStepId && this.isSpecialKanbanStep(this.applicant.currentStepId)) {
            this.addStepToAvailableList(this.applicant.currentStepId);
        } 
        else if (mappedKanbanStepId && !this.applicant.currentStepId) {
            this.addStepToAvailableList(mappedKanbanStepId);
            this.applicant.currentStepId = mappedKanbanStepId;
        }
    }

    private setCurrentStepAndDetectChanges(mappedKanbanStepId: string): void {
        if (this.applicant.availableStepList.length > 0) {
            const currentStep = this.applicant.availableStepList.find(step => step.stepId === this.applicant.currentStepId);
            
            setTimeout(() => {
                if (currentStep) {
                    this.currentStepIdTemp = currentStep.stepId;
                } else if (mappedKanbanStepId) {
                    this.addStepToAvailableList(mappedKanbanStepId);
                    this.currentStepIdTemp = mappedKanbanStepId;
                    this.applicant.currentStepId = mappedKanbanStepId;
                } else {
                    this.currentStepIdTemp = this.applicant.availableStepList[0].stepId;
                }
                this.cdr.detectChanges();
            }, 0);
        } else if (mappedKanbanStepId) {
            this.applicant.availableStepList = [];
            this.addStepToAvailableList(mappedKanbanStepId);
            this.currentStepIdTemp = mappedKanbanStepId;
            this.applicant.currentStepId = mappedKanbanStepId;
            
            setTimeout(() => {
                this.cdr.detectChanges();
            }, 0);
        }
    }

    async getApplicationProfileData() {
        try {
            this.loading = true;
            this.applicant = await firstValueFrom(this.applicationService.GetApplicationUserInfoData({ id: this.applicationId }));
            
            const mappedKanbanStepId = this.mapApplicationStatusToKanbanStep(this.applicant.applicationStatus);
            
            if ((!this.applicant.currentStepId || this.applicant.currentStepId === 'null') && mappedKanbanStepId) {
                this.applicant.currentStepId = mappedKanbanStepId;
            }
            this.updateStepChangeDisabled();
            
            if (!this.applicant.availableStepList) {
                this.applicant.availableStepList = [];
            }
            
            this.handleCurrentStep(mappedKanbanStepId);
            this.setCurrentStepAndDetectChanges(mappedKanbanStepId);
            this.lastActivityDiff();
        } catch (error) {
            console.error('Error loading application profile data', error);
        } finally {
            this.loading = false;
        }
    }

    openSocialPlatformDialog() {
        this.socialPlatformsDialogVisible = !this.socialPlatformsDialogVisible;
    }

    setApplicantUrlList() {
        const order = [
            EGeneralApplicationFormAccountLinkType.Linkedin,
            EGeneralApplicationFormAccountLinkType.Github,
            EGeneralApplicationFormAccountLinkType.Behance,
            EGeneralApplicationFormAccountLinkType.Dribbble,
            EGeneralApplicationFormAccountLinkType.Medium,
        ];

        this.applicant.socialPlatformListForProfile = this.applicant.socialPlatformListForProfile ?? false ? this.applicant.socialPlatformListForProfile
        .filter(x => !!x?.socialPlatformURL?.trim())
        .map(x => ({
            ...x,
            fullUrl: this.getAccountFullURL(x.socialPlatformLinkType, x.socialPlatformURL),
            svgIconPath: this.iconPath + this.getSocialSvgIconRef(x.socialPlatformLinkType) + this.iconPathEnding
        }))
        .filter((x): x is NonNullable<typeof x> => !!x)
        .sort((a, b) => {
            const ia = order.indexOf(a.socialPlatformLinkType);
            const ib = order.indexOf(b.socialPlatformLinkType);
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib); // listede yoksa sona
        }) : [];

    }

    getSocialSvgIconRef(type) {
        return Object.keys(AccountLinkTypeEnum)[Object.values(AccountLinkTypeEnum).indexOf(type)].toLowerCase();
    }

    getAccountFullURL(typeEnum, url) {
        let type = Object.keys(AccountLinkTypeEnum)[Object.values(AccountLinkTypeEnum).indexOf(typeEnum)];
        let urlKey = (EGeneralApplicationFormAccountLinkTypeURL as any)[type] as string | undefined;
        return urlKey + url;
    }
    

    copyText(email: string) {
        this.isEmailCopied = true;
        const textArea = document.createElement('textarea');
        textArea.value = email;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        setTimeout(() => {
            this.isEmailCopied = false;
        }, 1000);

    }

    getStepColor(stepId: string): string {
        switch (stepId) {
            case '-1':
                return '#2962FF';
            case '-2':
                return '#7173AC';
            case '-3':
                return '#F20505';
            case '-4':
                return '#E9B500';
            case '-5':
                return '#5ED44A';
            case '-6':
                return '#df739c';
            default:
                return '#2962FF';
        }
    }

    private translateAvailableStepListTitles(): void {
        if (!this.applicant?.availableStepList?.length) return;
    
        this.applicant.availableStepList = this.applicant.availableStepList.map(step => {
            return {
                ...step,
                title: this.isSpecialKanbanStep(step.stepId) ?  this.getKanbanStepTitle(step.stepId) : step.title 
            };
        });
    }
}
